import { ruleOffsetSecondsAt, zonedWallClock } from "../time-zone-rules";

type LocalDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
};

const LOCAL_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const HOUR_MS = 60 * 60 * 1_000;

function utcDate(parts: LocalDateTimeParts): Date {
  const date = new Date(0);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  date.setUTCHours(
    parts.hour,
    parts.minute,
    parts.second,
    parts.millisecond,
  );
  return date;
}

function isSameUtcParts(date: Date, parts: LocalDateTimeParts): boolean {
  return (
    date.getUTCFullYear() === parts.year &&
    date.getUTCMonth() + 1 === parts.month &&
    date.getUTCDate() === parts.day &&
    date.getUTCHours() === parts.hour &&
    date.getUTCMinutes() === parts.minute &&
    date.getUTCSeconds() === parts.second &&
    date.getUTCMilliseconds() === parts.millisecond
  );
}

function parseLocalDateTime(value: string): LocalDateTimeParts | null {
  const match = LOCAL_DATE_TIME_PATTERN.exec(value);
  if (!match) return null;

  const parts: LocalDateTimeParts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? "0"),
    millisecond: Number((match[7] ?? "0").padEnd(3, "0")),
  };
  if (parts.year === 0 || !isSameUtcParts(utcDate(parts), parts)) return null;
  return parts;
}

function dateTimeFormatter(timezone: string): Intl.DateTimeFormat | null {
  if (timezone.startsWith("+") || timezone.startsWith("-")) return null;
  try {
    return new Intl.DateTimeFormat("en-GB-u-ca-gregory-nu-latn", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return null;
  }
}

function zonedParts(
  formatter: Intl.DateTimeFormat,
  date: Date,
  timezone: string,
): Omit<LocalDateTimeParts, "millisecond"> | null {
  // tzdata 2026e's changes over the runtime's data (lib/time-zone-rules.ts).
  if (ruleOffsetSecondsAt(date.getTime(), timezone) !== null) return zonedWallClock(date.getTime(), timezone);
  let year: number | null = null;
  let month: number | null = null;
  let day: number | null = null;
  let hour: number | null = null;
  let minute: number | null = null;
  let second: number | null = null;

  for (const part of formatter.formatToParts(date)) {
    const numeric = Number(part.value);
    if (part.type === "year") year = numeric;
    if (part.type === "month") month = numeric;
    if (part.type === "day") day = numeric;
    if (part.type === "hour") hour = numeric;
    if (part.type === "minute") minute = numeric;
    if (part.type === "second") second = numeric;
  }

  if (
    year === null ||
    month === null ||
    day === null ||
    hour === null ||
    minute === null ||
    second === null
  ) {
    return null;
  }
  return { year, month, day, hour, minute, second };
}

function localPartsAsUtc(parts: Omit<LocalDateTimeParts, "millisecond">): number {
  return utcDate({ ...parts, millisecond: 0 }).getTime();
}

function matchesZonedParts(
  actual: Omit<LocalDateTimeParts, "millisecond">,
  expected: LocalDateTimeParts,
): boolean {
  return (
    actual.year === expected.year &&
    actual.month === expected.month &&
    actual.day === expected.day &&
    actual.hour === expected.hour &&
    actual.minute === expected.minute &&
    actual.second === expected.second
  );
}

function parseIsoDate(value: string): LocalDateTimeParts | null {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return null;
  const parts: LocalDateTimeParts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  };
  if (parts.year === 0 || !isSameUtcParts(utcDate(parts), parts)) return null;
  return parts;
}

export function localDateInTimeZone(now: Date, timezone: string): string {
  if (!Number.isFinite(now.getTime())) throw new RangeError("Invalid date");
  const formatter = dateTimeFormatter(timezone);
  if (!formatter) throw new RangeError("Invalid timezone");
  const parts = zonedParts(formatter, now, timezone);
  if (!parts) throw new RangeError("Unable to resolve local date");
  return `${String(parts.year).padStart(4, "0")}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

export function zonedLocalDateTimeToUtc(
  localDateTime: string,
  timezone: string,
): Date | null {
  const expected = parseLocalDateTime(localDateTime);
  const formatter = dateTimeFormatter(timezone);
  if (!expected || !formatter) return null;

  const localEpoch = utcDate(expected).getTime();
  const sampleEpoch = localEpoch - expected.millisecond;
  const offsets = new Set<number>();

  for (let hours = -48; hours <= 48; hours += 6) {
    const sample = new Date(sampleEpoch + hours * HOUR_MS);
    const sampleParts = zonedParts(formatter, sample, timezone);
    if (sampleParts) {
      offsets.add(localPartsAsUtc(sampleParts) - sample.getTime());
    }
  }

  const matches = new Map<number, Date>();
  for (const offset of offsets) {
    const candidate = new Date(localEpoch - offset);
    const candidateParts = zonedParts(formatter, candidate, timezone);
    if (
      candidateParts &&
      candidate.getUTCMilliseconds() === expected.millisecond &&
      matchesZonedParts(candidateParts, expected)
    ) {
      matches.set(candidate.getTime(), candidate);
    }
  }

  return matches.size === 1 ? [...matches.values()][0] : null;
}

export function addDaysToIsoDate(date: string, days: number): string {
  const parts = parseIsoDate(date);
  if (!parts || !Number.isInteger(days)) throw new RangeError("Invalid ISO date");
  const result = utcDate(parts);
  result.setUTCDate(result.getUTCDate() + days);
  return result.toISOString().slice(0, 10);
}

export function localDiscoveryBoundary(
  now: Date,
  timezone: string,
  dayCount: number,
): Date {
  if (!Number.isInteger(dayCount)) throw new RangeError("Invalid day count");
  const localDate = localDateInTimeZone(now, timezone);
  const boundaryDate = addDaysToIsoDate(localDate, dayCount);
  const boundary = zonedLocalDateTimeToUtc(`${boundaryDate}T00:00`, timezone);
  if (!boundary) throw new RangeError("Unable to resolve local boundary");
  return boundary;
}
